import React, { useState, useEffect } from 'react';
import { MessageCircle, CheckCircle, Circle } from 'lucide-react';
import type { AudioComment } from '../services/audioCommentService';

interface AudioCommentMarkerProps {
  comment: AudioComment;
  position: number; // Position as percentage (0-100) on the waveform
  isActive: boolean;
  onClick: () => void;
  canComment: boolean;
  currentUserId?: string;
  currentAudioTime?: number; // Current playback time in seconds (for animation)
  audioDuration?: number; // Total audio duration in seconds
}

const AudioCommentMarker: React.FC<AudioCommentMarkerProps> = ({
  comment,
  position,
  isActive,
  onClick,
  currentAudioTime = 0,
  audioDuration = 0,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipOpacity, setTooltipOpacity] = useState(0);
  
  const hasReplies = comment.replies && comment.replies.length > 0;
  const isResolved = comment.resolved;
  
  // Check if audio playback is currently at or near this comment's timestamp
  const commentTimeSeconds = comment.timestampMs / 1000;
  const timeDifference = Math.abs(currentAudioTime - commentTimeSeconds);
  const isPlayingAtComment = !isResolved && currentAudioTime > 0 && timeDifference < 1.5; // Within 1.5 seconds (longer window)
  
  // Handle tooltip show/hide with smooth transitions
  useEffect(() => {
    if (isPlayingAtComment) {
      // Show tooltip with fade-in
      setShowTooltip(true);
      setTimeout(() => setTooltipOpacity(1), 10); // Small delay for smooth fade-in
    } else {
      // Hide tooltip with fade-out
      setTooltipOpacity(0);
      // Wait for fade-out animation to complete before hiding
      const timer = setTimeout(() => {
        setShowTooltip(false);
      }, 300); // Match the transition duration
      return () => clearTimeout(timer);
    }
  }, [isPlayingAtComment]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick();
  };

  const getMarkerColor = () => {
    if (isResolved) return 'var(--color-muted)';
    if (isPlayingAtComment) return 'var(--collab-blue-border)';
    if (isActive) return 'var(--collab-blue-border)';
    if (isHovered) return 'var(--collab-blue-border)';
    return 'var(--collab-blue-border)'; // Blue - contrasts with both gray waveform and green progress
  };

  const getMarkerBackground = () => {
    if (isResolved) return 'var(--color-hover)';
    if (isPlayingAtComment) return 'var(--collab-blue-bg)';
    if (isActive) return 'var(--collab-blue-bg)';
    if (isHovered) return 'var(--collab-blue-hover)';
    return 'var(--collab-blue-bg)';
  };

  return (
    <div
      className="absolute top-0 transform -translate-x-1/2 cursor-pointer group pointer-events-auto z-10"
      style={{ left: `${position}%`, height: '64px' }}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Playback tooltip - shows comment text when audio is playing at this timestamp (SoundCloud style) */}
      {showTooltip && (
        <div
          className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 px-3 py-2 rounded shadow-lg pointer-events-none transition-opacity duration-300"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            fontSize: '0.75rem',
            zIndex: 10000,
            maxWidth: '250px',
            minWidth: '120px',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            opacity: tooltipOpacity
          }}
        >
          <p className="text-white text-center leading-relaxed" style={{ maxWidth: '250px' }}>
            {comment.text}
          </p>
          {/* Arrow pointing down */}
          <div
            className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1"
            style={{
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '6px solid rgba(0, 0, 0, 0.9)',
              opacity: tooltipOpacity
            }}
          />
        </div>
      )}
      
      {/* Comment marker line extending upward from progress bar */}
      <div
        className="w-0.5 h-8 transition-all duration-200"
        style={{ 
          backgroundColor: getMarkerColor(),
          transform: isHovered || isPlayingAtComment ? 'scaleY(1.2)' : 'scaleY(1)',
          boxShadow: isPlayingAtComment ? '0 0 8px var(--collab-blue-border)' : 'none'
        }}
      />
      
      {/* Comment marker dot at top of line */}
      <div
        className={`absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1 w-4 h-4 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${isPlayingAtComment ? 'animate-pulse' : ''}`}
        style={{
          backgroundColor: getMarkerBackground(),
          borderColor: getMarkerColor(),
          transform: isHovered || isActive || isPlayingAtComment ? 'scale(1.3)' : 'scale(1)',
          boxShadow: isPlayingAtComment ? '0 0 12px var(--collab-blue-border)' : 'none'
        }}
      >
        {isResolved ? (
          <CheckCircle className="w-2.5 h-2.5" style={{ color: 'var(--info-green-text)' }} />
        ) : hasReplies ? (
          <MessageCircle className="w-2.5 h-2.5" style={{ color: 'var(--collab-blue-text)' }} />
        ) : (
          <Circle className="w-2.5 h-2.5" style={{ color: 'var(--collab-blue-text)' }} />
        )}
      </div>
    </div>
  );
};

export default AudioCommentMarker;
